import type {
  UpdatingCriteriasState,
  UpdatingDataCriteria,
} from '~/components/Profile/model';
import type { CriteriaChoiceType, CriteriaChoiceUserType } from '~/model/CriteriaChoiceType';
import type { CriteriaType } from '~/model/CriteriaType';
import { useLocale } from 'next-intl';
import { useCallback, useState } from 'react';
import { fetchCollection, fetchItem } from '@/app/actions/server';
import { useApiErrorContext } from '@/app/components/Provider';
import { AcceptLanguage } from '~/const/locale';
import { useRequestState } from '~/hooks/request/useRequestState';

export function useCriterion({
  criterion,
  userCriteria,
}: {
  criterion?: CriteriaType;
  userCriteria?: CriteriaChoiceUserType[];
}) {
  const locale = useLocale();

  const { startRequest, handleSuccess, handleError } = useRequestState();
  const { setError } = useApiErrorContext();

  const [updatingData, setUpdatingData] = useState<UpdatingCriteriasState>({});
  const [activeCriterion, setActiveCriterion] = useState<CriteriaType>(criterion);

  const resetUpdatingData = () => setUpdatingData({});

  const onChange = ({
    value,
    type,
    criteriaUri,
    ignore,
  }: UpdatingDataCriteria) => {
    const userCriteriaChoiceId = userCriteria?.find(
      uC => uC.criterion['@id'] === criteriaUri,
    )?.id;

    setUpdatingData(state => ({
      ...state,
      [criteriaUri]: {
        criteriaUri,
        type,
        value,
        ignore,
        ...(userCriteriaChoiceId && {
          userCriteriaChoiceId,
        }),
      },
    }));
  };

  const getCriterion = async (id: number) => {
    if (!id)
      return;

    const response = await fetchItem<CriteriaType>({
      pathname: `criteria/${id}`,
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      revalidate: 3600,
    });

    if (response['hydra:description']) {
      return setError(response);
    }

    setActiveCriterion(response);
  };

  const getCriterionChoices = useCallback(async () => {
    if (!criterion?.id || !!criterion?.choices)
      return;

    startRequest();
    const response = await fetchCollection<CriteriaChoiceType>({
      pathname: `criteria/${criterion.id}/choices`,
      headers: {
        acceptLanguage: AcceptLanguage[locale],
      },
      revalidate: 3600,
      unpaginate: true,
    });

    if (response['hydra:description']) {
      handleError();
      return setError(response);
    }

    const criterionWithChoice = { ...criterion, choices: response['hydra:member'] };
    setActiveCriterion(criterionWithChoice);
    handleSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criterion]);

  return {
    criterionWithChoices: activeCriterion,
    updatingData,
    getCriterion,
    getCriterionChoices,
    onChange,
    resetUpdatingData,
  };
}
